<template>
  <div class="bg-kiosk px-[68px] font-sans">
    <NuxtLoadingIndicator />
    <div class="relative flex min-h-[1920px] flex-col pt-[60px] text-primary">
      <div class="w-full">
        <img
          src="~/assets/kiosk/logo.png"
          class="h-[120px] w-[707px]"
          alt="laboratoria logo"
        />
      </div>
      <div class="my-6 flex justify-center">
        <div class="flex w-full flex-col">
          <a href="" @click.prevent="goBack()">
            <div class="flex items-center">
              <inline-svg
                :src="useAsset(`kiosk/arrow.svg`)"
                class="inline h-[30px]"
                loading="lazy"
                alt="icon"
              />
              <span class="text-[25px] font-medium text-primaryLight"
                >Wróć</span
              >
            </div>
          </a>
        </div>
      </div>
      <ClientOnly>
        <SharedDataIframe />
        <LazyModalsIdleModal v-if="isFinished" />
      </ClientOnly>
      <div
        class="relative flex flex-grow items-start justify-center overflow-hidden px-4 md:px-6"
      >
        <div class="w-full">
          <slot />
        </div>
      </div>
    </div>
    <ClientOnly>
      <LazyModalsConfirmModal v-if="isFinished" />
    </ClientOnly>
  </div>
</template>

<script setup>
import { useAuthStore } from '~~/stores/auth'
import { useFeaturesStore } from '~~/stores/features'
import { useModalsStore } from '~/stores/modals'
import { useKioskStore } from '~/stores/kiosk'
import { useCartStore } from '~/stores/cart'
import InlineSvg from 'vue-inline-svg'

const {
  public: { baseUrl },
} = useRuntimeConfig()
const { $echo } = useNuxtApp()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const nuxtApp = useNuxtApp()
const kioskStore = useKioskStore()
const cartStore = useCartStore()
const isFinished = ref(false)

const goBack = () => {
  if (route.path.split('/payment/').length === 2) {
    kioskStore.clearStore()
    cartStore.clearItems()
    navigateTo({ path: '/kiosk' })
  } else {
    router.back()
  }
}

const i18nHead = useLocaleHead({
  addDirAttribute: true,
  identifierAttribute: 'id',
  addSeoAttributes: false,
})

const featuresStore = useFeaturesStore()

await featuresStore.fetchFeatures()

useHead(() => ({
  titleTemplate: '%s - ALAB laboratoria',
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs.lang,
  },
  link: [
    {
      hid: 'canonical',
      rel: 'canonical',
      href: `${baseUrl}${route.path}`,
    },
    ...(i18nHead.value.link || []),
  ],
  meta: [
    ...(i18nHead.value.meta || []),
    ...useGenerateSeoMeta(
      `ALAB - ${t('homepage.diagnostic_laboratories')}`,
      'AALAB laboratoria to ogólnopolska sieć laboratoriów diagnostycznych przeprowadzających ponad 90 mln badań (m.in. krwi, moczu) rocznie. Naszym Pacjentom udostępniamy ponad 700 Punktów Pobrań w Warszawie, Łodzi, Katowicach, Wrocławiu, Krakowie, Poznaniu i wielu innych miastach.'
    ),
    { name: 'robots', content: 'noindex' },
  ],
}))

const authStore = useAuthStore()
const moadlStore = useModalsStore()

const count = ref(30)
const tick = ref(0)

const idleModalDisplayedInSec = 30

const resetTimeout = () => {
  count.value = idleModalDisplayedInSec
}

watch(tick, (value) => {
  setTimeout(() => {
    tick.value = value + 1
    if (
      window.location.pathname !== '/kiosk' &&
      window.location.pathname.split('/pay/').length === 1 &&
      !moadlStore.isOpenModal('idleModal')
    ) {
      count.value = count.value - 1
    } else {
      count.value = idleModalDisplayedInSec
    }
  }, 1000)
  if (count.value <= 0) {
    count.value = idleModalDisplayedInSec
    moadlStore.open('idleModal')
  }
})

onMounted(() => {
  resetTimeout()
  setTimeout(() => (tick.value = tick.value + 1))
  window.onload = resetTimeout
  document.onmousemove = resetTimeout
  document.onkeydown = resetTimeout
})

const gtm = useGtm()

const initUserStatusUpdateListener = () => {
  $echo
    .channel(`notification.${authStore.user?.id}`)
    .listen('UserStatusUpdate', () => {
      router.push({
        path: '/error/403',
      })
      moadlStore.open('blockedPatientModal')
    })
}

watch(
  () => authStore.user,
  (newValue) => {
    if (newValue?.id) {
      gtm.trackEvent({
        user_id: newValue.id,
      })
      if ($echo && process.client) {
        initUserStatusUpdateListener()
      }
    }
  }
)

nuxtApp.hook('page:finish', () => {
  setTimeout(() => {
    isFinished.value = true
  }, 500)

  window.scrollTo(0, 0)
})
</script>

<style lang="scss" scoped>
.bg-kiosk {
  background: url('~/assets/kiosk/background.jpg') no-repeat center top fixed;
  -webkit-background-size: cover;
  -moz-background-size: cover;
  -o-background-size: cover;
  background-size: cover;
}
</style>
